import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';

import {
  POLL_TYPE_MULTI_SELECT,
  POLL_TYPE_SINGLE_CHOICE,
} from './pollFormValidation';

import css from './PollCard.module.css';

const RadioUnchecked = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="29"
    height="29"
    viewBox="0 0 29 29"
    fill="none"
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M14.1667 25.5C20.4259 25.5 25.5 20.4259 25.5 14.1667C25.5 7.90744 20.4259 2.83333 14.1667 2.83333C7.90744 2.83333 2.83333 7.90744 2.83333 14.1667C2.83333 20.4259 7.90744 25.5 14.1667 25.5ZM14.1667 28.3333C21.9907 28.3333 28.3333 21.9907 28.3333 14.1667C28.3333 6.34263 21.9907 0 14.1667 0C6.34263 0 0 6.34263 0 14.1667C0 21.9907 6.34263 28.3333 14.1667 28.3333Z"
      fill="#CACACE"
    />
  </svg>
);

const RadioChecked = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="29"
    height="29"
    viewBox="0 0 29 29"
    fill="none"
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M14.1667 0C6.34667 0 0 6.34667 0 14.1667C0 21.9867 6.34667 28.3333 14.1667 28.3333C21.9867 28.3333 28.3333 21.9867 28.3333 14.1667C28.3333 6.34667 21.9867 0 14.1667 0ZM14.1667 25.5C7.905 25.5 2.83333 20.4283 2.83333 14.1667C2.83333 7.905 7.905 2.83333 14.1667 2.83333C20.4283 2.83333 25.5 7.905 25.5 14.1667C25.5 20.4283 20.4283 25.5 14.1667 25.5ZM14.1667 21.25C18.0787 21.25 21.25 18.0787 21.25 14.1667C21.25 10.2546 18.0787 7.08333 14.1667 7.08333C10.2546 7.08333 7.08333 10.2546 7.08333 14.1667C7.08333 18.0787 10.2546 21.25 14.1667 21.25Z"
      fill="#2F0F48"
    />
  </svg>
);

const CheckboxUnchecked = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="29"
    height="29"
    viewBox="0 0 29 29"
    fill="none"
    aria-hidden="true"
  >
    <rect
      x="2.5"
      y="2.5"
      width="24"
      height="24"
      rx="5.5"
      fill="transparent"
      stroke="#CACACE"
      strokeWidth="3"
    />
  </svg>
);

const CheckboxChecked = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="29"
    height="29"
    viewBox="0 0 29 29"
    fill="none"
    aria-hidden="true"
  >
    <rect x="2" y="2" width="25" height="25" rx="6" fill="#2F0F48" />
    <path
      d="M8.5 14.5L12.4 18.4L20.8 10"
      stroke="#FFFFFF"
      strokeWidth="3.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const POLL_STATUS_OPEN = 'open';
const POLL_STATUS_CLOSED = 'closed';

const RESULT_RANK_CLASSES = [
  'resultBarRank1',
  'resultBarRank2',
  'resultBarRank3',
  'resultBarRank4',
];

export const formatPollPercentage = value => {
  const numberValue = Number(value);

  if (!Number.isFinite(numberValue)) {
    return '0%';
  }

  return Number.isInteger(numberValue)
    ? `${numberValue}%`
    : `${numberValue.toFixed(2)}%`;
};

const getSafePercentage = value => {
  const numberValue = Number(value);

  if (!Number.isFinite(numberValue)) {
    return 0;
  }

  return Math.min(100, Math.max(0, numberValue));
};

export const getSanitizedPollForRender = poll => {
  if (!poll || !poll.id || !Array.isArray(poll.options)) {
    return null;
  }

  const pollType = [
    POLL_TYPE_SINGLE_CHOICE,
    POLL_TYPE_MULTI_SELECT,
  ].includes(poll.pollType)
    ? poll.pollType
    : POLL_TYPE_SINGLE_CHOICE;

  const status = [
    POLL_STATUS_OPEN,
    POLL_STATUS_CLOSED,
  ].includes(poll.status)
    ? poll.status
    : POLL_STATUS_OPEN;

  const seenOptionIds = new Set();

  const options = poll.options
    .filter(option => option?.optionId && typeof option.text === 'string')
    .filter(option => {
      const optionId = String(option.optionId);

      if (seenOptionIds.has(optionId)) {
        return false;
      }

      seenOptionIds.add(optionId);

      return true;
    })
    .sort(
      (a, b) =>
        Number(a.order || 0) - Number(b.order || 0)
    )
    .map(option => ({
      optionId: String(option.optionId),
      text: option.text,
      order: Number(option.order || 0),
      percentage: getSafePercentage(option.percentage),
      voteCount: Number(option.voteCount || 0),
    }));

  if (!options.length) {
    return null;
  }

  const suppliedTotalSelections = Number(
    poll.totalSelections || 0
  );

  const optionTotalSelections = options.reduce(
    (total, option) =>
      total + Number(option.voteCount || 0),
    0
  );

  return {
    ...poll,
    pollType,
    status,
    options,

    totalSelections:
      suppliedTotalSelections || optionTotalSelections,

    currentUser: {
      hasVoted: Boolean(
        poll.currentUser?.hasVoted
      ),

      selectedOptionIds: Array.isArray(
        poll.currentUser?.selectedOptionIds
      )
        ? poll.currentUser.selectedOptionIds.map(String)
        : [],

      isOwner: Boolean(
        poll.currentUser?.isOwner
      ),

      canVote: Boolean(
        poll.currentUser?.canVote
      ),
    },
  };
};

const PollClosedIndicator = () => (
  <span
    className={css.closedIndicator}
    aria-label="Poll closed"
  >
    Poll closed
  </span>
);

const PollOption = props => {
  const {
    option,
    pollType,
    disabled,
    selected,
    onToggle,
    isClosed,
    isWinning,
    resultRank,
  } = props;

  const isMultiSelect =
    pollType === POLL_TYPE_MULTI_SELECT;

  const controlIcon = isMultiSelect ? (
    selected ? (
      <CheckboxChecked />
    ) : (
      <CheckboxUnchecked />
    )
  ) : selected ? (
    <RadioChecked />
  ) : (
    <RadioUnchecked />
  );

  const safeRank = Math.min(
    resultRank,
    RESULT_RANK_CLASSES.length - 1
  );

  const rankClass =
    RESULT_RANK_CLASSES[safeRank];

  return (
    <button
      type="button"
      className={classNames(
        css.optionRow,
        {
          [css.optionRowSelected]: selected,
          [css.optionRowDisabled]: disabled,
          [css.optionRowClosed]: isClosed,
          [css.optionRowWinning]:
            isClosed && isWinning,
        }
      )}
      disabled={disabled}
      onClick={() =>
        onToggle(option.optionId)
      }
      aria-pressed={selected}
      aria-label={
        isClosed
          ? `${option.text}, ${formatPollPercentage(
              option.percentage
            )}, ${option.voteCount} ${
              option.voteCount === 1
                ? 'vote'
                : 'votes'
            }`
          : `${option.text}, ${formatPollPercentage(
              option.percentage
            )}`
      }
    >
      {!isClosed ? (
        <span
          className={classNames(
            css.resultBar,
            {
              [css.resultBarSelected]:
                selected,
            }
          )}
          style={{
            width: `${option.percentage}%`,
          }}
          aria-hidden="true"
        />
      ) : (
        <span
          className={classNames(
            css.resultBar,
            css[rankClass]
          )}
          style={{
            width: `${option.percentage}%`,
          }}
          aria-hidden="true"
        />
      )}

      {!isClosed ? (
        <span
          className={css.optionControl}
        >
          {controlIcon}
        </span>
      ) : null}

      <span className={css.optionContent}>
        <span className={css.optionText}>
          {option.text}
        </span>
      </span>

      <span className={css.optionPercentage}>
        {formatPollPercentage(
          option.percentage
        )}
      </span>
    </button>
  );
};

const getPollTypeLabel = pollType =>
  pollType === POLL_TYPE_MULTI_SELECT
    ? 'Select all that apply'
    : 'Select one';

const sortResultOptions = options =>
  [...options].sort((a, b) => {
    const voteDifference =
      Number(b.voteCount || 0) -
      Number(a.voteCount || 0);

    if (voteDifference !== 0) {
      return voteDifference;
    }

    const percentageDifference =
      Number(b.percentage || 0) -
      Number(a.percentage || 0);

    if (percentageDifference !== 0) {
      return percentageDifference;
    }

    return (
      Number(a.order || 0) -
      Number(b.order || 0)
    );
  });

/**
 * Calculate rank using vote count.
 *
 * Example:
 *
 * 50%, 50%
 * rank 0, rank 0
 *
 * 60%, 30%, 10%
 * rank 0, rank 1, rank 2
 *
 * 40%, 40%, 20%
 * rank 0, rank 0, rank 1
 */
const getResultRank = (
  options,
  currentOption
) => {
  const uniqueVoteCounts = [
    ...new Set(
      options.map(option =>
        Number(option.voteCount || 0)
      )
    ),
  ].sort((a, b) => b - a);

  const currentVoteCount = Number(
    currentOption.voteCount || 0
  );

  const rank =
    uniqueVoteCounts.indexOf(
      currentVoteCount
    );

  return rank === -1 ? 0 : rank;
};

const PollCard = props => {
  const {
    poll,
    hydrationError,
    voteInProgress = false,
    closeInProgress = false,
    errorMessage,
    onVote,
    onClose,
    currentVoterProfile,
  } = props;

  const [
    responsePoll,
    setResponsePoll,
  ] = useState(null);

  const [
    localVoteInProgress,
    setLocalVoteInProgress,
  ] = useState(false);

  const [
    localCloseInProgress,
    setLocalCloseInProgress,
  ] = useState(false);

  const [
    isCloseConfirmationOpen,
    setIsCloseConfirmationOpen,
  ] = useState(false);

  const renderPoll =
    responsePoll?.id &&
    responsePoll.id === poll?.id
      ? responsePoll
      : poll;

  const sanitizedPoll = useMemo(
    () =>
      getSanitizedPollForRender(
        renderPoll
      ),
    [renderPoll]
  );

  const selectedFromServer =
    sanitizedPoll?.currentUser
      ?.selectedOptionIds || [];

  const [
    selectedOptionIds,
    setSelectedOptionIds,
  ] = useState(selectedFromServer);

  useEffect(() => {
    setResponsePoll(null);
  }, [poll]);

  useEffect(() => {
    setSelectedOptionIds(
      selectedFromServer
    );
  }, [
    sanitizedPoll?.id,
    selectedFromServer.join('|'),
  ]);

  useEffect(() => {
    setIsCloseConfirmationOpen(
      false
    );
  }, [
    sanitizedPoll?.id,
    sanitizedPoll?.status,
  ]);

  if (hydrationError) {
    return (
      <div
        className={css.pollFallback}
        role="status"
      >
        Poll unavailable. Please
        refresh to try again.
      </div>
    );
  }

  if (!sanitizedPoll) {
    if (poll) {
      console.warn(
        '[PollCard] Malformed poll data skipped',
        {
          pollId:
            poll?.id || null,

          circlePostId:
            poll?.circlePostId || null,
        }
      );

      return (
        <div
          className={
            css.pollFallback
          }
          role="status"
        >
          Poll unavailable. Please
          refresh to try again.
        </div>
      );
    }

    return null;
  }

  const {
    currentUser,
    options,
    pollType,
    status,
    title,
  } = sanitizedPoll;

  const isClosed =
    status === POLL_STATUS_CLOSED;

  const pollTypeLabel =
    getPollTypeLabel(pollType);

  const displayOptions = isClosed
    ? sortResultOptions(options)
    : options;

  const effectiveVoteInProgress =
    voteInProgress ||
    localVoteInProgress;

  const effectiveCloseInProgress =
    closeInProgress ||
    localCloseInProgress;

  const isActionInProgress =
    effectiveVoteInProgress ||
    effectiveCloseInProgress;

  const canSelect =
    status === POLL_STATUS_OPEN &&
    !currentUser.hasVoted &&
    currentUser.canVote &&
    !isActionInProgress;

  const canVote =
    canSelect &&
    selectedOptionIds.length > 0;

  const showVoteButton =
    status === POLL_STATUS_OPEN &&
    !currentUser.hasVoted &&
    currentUser.canVote;

  const showOwnerAction =
    status === POLL_STATUS_OPEN &&
    currentUser.isOwner;

  const winningVoteCount = isClosed
    ? Math.max(
        ...options.map(option =>
          Number(
            option.voteCount || 0
          )
        )
      )
    : 0;

  const handleToggle = optionId => {
    if (!canSelect) {
      return;
    }

    setSelectedOptionIds(
      prevSelected => {
        if (
          pollType ===
          POLL_TYPE_SINGLE_CHOICE
        ) {
          return [optionId];
        }

        return prevSelected.includes(
          optionId
        )
          ? prevSelected.filter(
              id => id !== optionId
            )
          : [
              ...prevSelected,
              optionId,
            ];
      }
    );
  };

  const handleVote = async () => {
    if (!canVote || !onVote) {
      return;
    }

    try {
      setLocalVoteInProgress(
        true
      );

      const updatedPoll =
        await onVote(
          sanitizedPoll.id,
          selectedOptionIds,
          currentVoterProfile
        );

      if (
        updatedPoll?.id ===
        sanitizedPoll.id
      ) {
        setResponsePoll(
          updatedPoll
        );
      }
    } finally {
      setLocalVoteInProgress(
        false
      );
    }
  };

  const handleClose = async () => {
    if (!onClose) {
      return;
    }

    try {
      setLocalCloseInProgress(
        true
      );

      const updatedPoll =
        await onClose(
          sanitizedPoll.id
        );

      if (
        updatedPoll?.id ===
        sanitizedPoll.id
      ) {
        setResponsePoll(
          updatedPoll
        );
      }
    } finally {
      setLocalCloseInProgress(
        false
      );
    }
  };

  return (
    <section
      className={css.pollCard}
      aria-label={title || 'Poll'}
    >
      <div
        className={css.pollHeader}
      >
        <span
          className={
            css.pollTypeLabel
          }
        >
          {pollTypeLabel}
        </span>

        {isClosed ? (
          <PollClosedIndicator />
        ) : currentUser.hasVoted ? (
          <span
            className={
              css.votedIndicator
            }
          >
            Voted
          </span>
        ) : null}
      </div>

      <div
        className={css.optionsList}
      >
        {displayOptions.map(
          option => {
            const resultRank =
              getResultRank(
                displayOptions,
                option
              );

            const isWinning =
              winningVoteCount >
                0 &&
              Number(
                option.voteCount || 0
              ) === winningVoteCount;

            return (
              <PollOption
                key={
                  option.optionId
                }
                option={option}
                pollType={
                  pollType
                }
                disabled={
                  !canSelect
                }
                selected={selectedOptionIds.includes(
                  option.optionId
                )}
                isClosed={
                  isClosed
                }
                isWinning={
                  isWinning
                }
                resultRank={
                  resultRank
                }
                onToggle={
                  handleToggle
                }
              />
            );
          }
        )}
      </div>

      {isClosed ? (
        <div
          className={
            css.pollResultsSummary
          }
        >
          {
            sanitizedPoll.totalSelections
          }{' '}
          {sanitizedPoll.totalSelections ===
          1
            ? 'vote'
            : 'votes'}{' '}
          · {pollTypeLabel}
        </div>
      ) : null}

      <div
        className={css.pollFooter}
      >
        {showVoteButton ? (
          <button
            type="button"
            className={
              css.voteButton
            }
            disabled={
              !canVote ||
              effectiveVoteInProgress ||
              !onVote
            }
            onClick={handleVote}
            aria-busy={
              effectiveVoteInProgress
            }
          >
            {effectiveVoteInProgress
              ? 'Submitting...'
              : 'Vote'}
          </button>
        ) : null}

        {showOwnerAction ? (
          <button
            type="button"
            className={
              css.ownerAction
            }
            disabled={
              effectiveCloseInProgress ||
              !onClose
            }
            onClick={() =>
              setIsCloseConfirmationOpen(
                true
              )
            }
            aria-expanded={
              isCloseConfirmationOpen
            }
            aria-controls={`poll-close-confirm-${sanitizedPoll.id}`}
            aria-busy={
              effectiveCloseInProgress
            }
          >
            {effectiveCloseInProgress
              ? 'Closing...'
              : 'End Poll & Publish Results'}
          </button>
        ) : null}
      </div>

      {isCloseConfirmationOpen &&
      showOwnerAction ? (
        <div
          id={`poll-close-confirm-${sanitizedPoll.id}`}
          className={
            css.closeConfirmation
          }
          role="dialog"
          aria-modal="false"
          aria-label="Confirm close poll"
        >
          <p
            className={
              css.closeConfirmationText
            }
          >
            Close this poll and
            publish the current
            results?
          </p>

          <div
            className={
              css.closeConfirmationActions
            }
          >
            <button
              type="button"
              className={
                css.cancelCloseButton
              }
              disabled={
                effectiveCloseInProgress
              }
              onClick={() =>
                setIsCloseConfirmationOpen(
                  false
                )
              }
            >
              Cancel
            </button>

            <button
              type="button"
              className={
                css.confirmCloseButton
              }
              disabled={
                effectiveCloseInProgress
              }
              onClick={
                handleClose
              }
            >
              {effectiveCloseInProgress
                ? 'Closing...'
                : 'End poll'}
            </button>
          </div>
        </div>
      ) : null}

      {errorMessage ? (
        <div
          className={css.pollError}
          role="alert"
        >
          {errorMessage}
        </div>
      ) : null}
    </section>
  );
};

export default PollCard;